import * as RD from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  hideTitle?: boolean;
  /** No X button; Escape and a click outside still close it. */
  hideClose?: boolean;
}

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl sm:w-[calc(100%-3rem)]',
};

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  size = 'md',
  hideTitle,
  hideClose,
}: DialogProps) {
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      {/* In fullscreen only the fullscreen element is drawn, so the dialog goes inside it. */}
      <RD.Portal container={document.fullscreenElement ?? undefined}>
        <RD.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm data-[state=open]:animate-[fade-in_200ms_ease-out] data-[state=closed]:animate-[fade-out_150ms_ease-in]" />
        <RD.Content
          className={cn(
            'fixed z-50 inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 w-full',
            'max-h-[92dvh] overflow-y-auto rounded-t-[28px] sm:rounded-[var(--radius-panel)] bg-bg-2 border border-line shadow-2xl shadow-black/60',
            'p-6 sm:p-8 data-[state=open]:animate-[dialog-in_260ms_var(--ease-out-soft)]',
            sizes[size],
            className,
          )}
        >
          <div className={cn('mb-5 pr-10', hideTitle && 'sr-only')}>
            <RD.Title className="font-display text-2xl font-semibold tracking-tight">
              {title}
            </RD.Title>
            {description && (
              <RD.Description className="mt-1.5 text-[15px] text-muted">
                {description}
              </RD.Description>
            )}
          </div>
          {!description && (
            <RD.Description className="sr-only">
              {typeof title === 'string' ? title : 'Dialog'}
            </RD.Description>
          )}
          {children}
          {!hideClose && (
            <RD.Close
              className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-muted hover:bg-white/8 hover:text-text"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </RD.Close>
          )}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
